import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router';
import { useForm } from 'react-hook-form';

import Modal from 'components/ui/Modal';
import { ConfirmButton } from 'components/ui/Button';
import { CHANGE_TRAVEL_PASS_DATES } from 'store/mutations';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { Column } from 'components/shared/Layout';

import { StyledLabel, StyledTextInput, StyledRow, StyledNoRevert } from '../SubscriptionDetails.styles';

export const ExtendSubscription = ({ onClose, travelDateEnd, travelPassReference, code, bookingReference, membershipNumber }) => {
  const { register, handleSubmit } = useForm();
  const history = useHistory();
  const location = useLocation();

  const [changeTravelPassDates, { loading: changeTravelPassLoading, error }] = useMutation(CHANGE_TRAVEL_PASS_DATES, {
    onCompleted: () => {
      onClose();

      history.replace({
        ...location,
        state: {
          shouldRetry: true
        }
      });
    },
    refetchQueries: ['profile']
  });
  const [extendValue, setExtendValue] = useState(1);
  const maxExtendMonthsWindow = /[6]/.test(code) ? 6 : 12;

  const handleOnSubmit = async (data) => {
    const newEndDate = moment(travelDateEnd).add(data.months, 'months');

    await changeTravelPassDates({
      variables: {
        travelPassReference,
        newEndDate: `${moment(newEndDate).format('YYYY-MM-DD')}T00:00:00.000Z`,
        bookingReference
      }
    });
  };

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/clubeurostar/${membershipNumber}#subscriptions`
    });
  }

  return (
    <Modal isOpen={true} title={<FormattedMessage id="subscriptions.extend" />} onClose={onClose}>
      <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="extend-subscription-form" data-testid="extend-subscription-form">
        <div>
          <StyledRow columns={12}>
            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.currentEndDate">
                <FormattedMessage id="subscriptions.currentEndDate" />:
              </StyledLabel>
              <StyledTextInput
                name="currentEndDate"
                type="text"
                value={moment(travelDateEnd).format('DD-MM-YYYY')}
                readOnly
                data-cy="subscription-currentEndDate"
                data-testid="subscription-currentEndDate"
              />
            </Column>

            <Column span={6}>
              <StyledLabel htmlFor="subscriptions.newEndDate">
                <FormattedMessage id="subscriptions.newEndDate" />:
              </StyledLabel>
              <StyledTextInput
                name="newEndDate"
                type="text"
                value={moment(travelDateEnd).add(extendValue, 'months').format('DD-MM-YYYY')}
                readOnly
                data-cy="subscription-newEndDate"
                data-testid="subscription-newEndDate"
              />
            </Column>
          </StyledRow>

          <StyledRow columns={12}>
            <Column span={12}>
              <StyledLabel htmlFor="subscriptions.numberOfMonths">
                <FormattedMessage id="subscriptions.numberOfMonths" />:
              </StyledLabel>
              <StyledTextInput
                style={{ width: '95%' }}
                data-cy={'extend-subscription-input'}
                data-testid={'extend-subscription-input'}
                type="number"
                inputMode="decimal"
                min={1}
                max={maxExtendMonthsWindow}
                step={1}
                value={extendValue}
                {...register('months', {
                  required: true,
                  onChange: (event) => setExtendValue(event.target.value)
                })}
              />
            </Column>
          </StyledRow>

          <StyledNoRevert data-testid={'subscriptions.monthsRange'}>
            <FormattedMessage id="subscriptions.monthsRange" values={{ maxMonths: maxExtendMonthsWindow }} />
          </StyledNoRevert>
        </div>

        <ConfirmButton
          type="submit"
          data-testid="submit"
          data-cy="submit"
          loading={changeTravelPassLoading}
          disabled={changeTravelPassLoading}
          variant="primary"
        >
          <FormattedMessage id="extras.hotel.modal.confirm" />
        </ConfirmButton>
      </form>
    </Modal>
  );
};

ExtendSubscription.propTypes = {
  onClose: PropTypes.func,
  travelDateEnd: PropTypes.string,
  travelPassReference: PropTypes.string,
  code: PropTypes.string,
  bookingReference: PropTypes.string,
  membershipNumber: PropTypes.string
};
